import { useEffect, useMemo, useRef, useState } from "react";
import { useGame } from "../store/useGame";
import type { Decision, DecisionOption, WorldState } from "../engine/types";
import {
  loadTropes,
  summarizeOption,
  type TropesLibrary,
} from "../engine/narratives";
import { INDICATORS } from "../engine/indicators";

interface Props {
  state: WorldState;
}

const ROMAN = ["I", "II", "III", "IV", "V", "VI"];

/**
 * Étiquette d'option — la maquette du 2026-08-06 en pose une (« Constitutionnel »,
 * « Statu quo »…). Aucun champ ne la porte : on la DÉRIVE de ce que l'option
 * fait réellement, plutôt que d'inventer 780 libellés à la main. L'ordre dit la
 * priorité : ce qui coûte de l'argent prime sur ce qui coûte du temps.
 */
function optionKind(opt: DecisionOption): string {
  if (opt.fiscal_impact) return "Budgétaire";
  if (opt.delayed_effects && (opt.delayed_effects as unknown[]).length > 0)
    return "À retardement";
  return opt.position === "primary" ? "Rupture" : "Mesure";
}

/**
 * Ligne « Coût » — dérivée du seul chiffre fiable dont on dispose, l'impact
 * budgétaire en Md€. Sans lui, on signale au moins qu'un effet est différé.
 * On ne rend rien plutôt que d'inventer un risque.
 */
function optionCost(
  opt: DecisionOption,
): { text: string; tone: "spend" | "earn" | "later" } | null {
  const f = opt.fiscal_impact;
  if (f) {
    const spend = f.delta_expense_billions ?? 0;
    const earn = f.delta_revenue_billions ?? 0;
    const net = earn - spend;
    if (Math.abs(net) >= 0.1) {
      const abs = Math.abs(net).toLocaleString("fr-FR", {
        maximumFractionDigits: 1,
      });
      return net < 0
        ? { text: `Coût : ${abs} Md€ sur le budget`, tone: "spend" }
        : { text: `Apport : ${abs} Md€ au budget`, tone: "earn" };
    }
  }
  if (opt.delayed_effects && (opt.delayed_effects as unknown[]).length > 0) {
    return { text: "Porte un effet différé", tone: "later" };
  }
  return null;
}

/**
 * Indicateurs hors-strip (révélés par décisions/seuils cumulés côté
 * engine) qu'on gate par défaut à T6 pour rester cohérent avec le
 * rythme onboarding. `gini_index` reste accessible (révélé par seuil
 * cumulé runtime ; on n'a pas de binding WASM pour interroger le
 * tracker, donc on l'autorise dès qu'on a un peu de matière).
 */
const REVEAL_BY_DECISION_AT_TURN: Record<string, number> = {
  approval_rate: 0,
  budget_balance: 0,
  gdp: 0,
  gini_index: 1,
  trust_in_institutions: 4,
  purchasing_power: 4,
  competitivite_industrielle: 6,
  innovation_index: 6,
  education_quality_index: 6,
  child_obesity_rate: 6,
  smoking_prevalence: 6,
  life_expectancy: 6,
  hospital_saturation: 6,
  housing_stress_index: 6,
  energy_poverty_rate: 6,
  energy_independence: 6,
  delinquancy_rate: 6,
  biodiversity_index: 8,
  carbon_intensity: 8,
};

/**
 * Bâtit le set des indicateurs *réellement révélés* à un tour donné.
 * Source de vérité côté front : `INDICATORS.revealedFromTurn` pour le
 * cockpit principal + `REVEAL_BY_DECISION_AT_TURN` pour les indicateurs
 * thématiques (révélés par décisions / seuils cumulés engine).
 *
 * Avant le fix, un `PERMISSIVE_REVEALED` constant exposait tout dès
 * T0 — le joueur recevait des prédictions sur `soft_power_index` à T1
 * alors que l'indicateur n'apparaît qu'à T6. Cohérence sensorielle
 * brisée. Désormais, les tropes ne mentionnent un indicateur que
 * lorsqu'il est effectivement visible.
 */
function revealedAt(turn: number): ReadonlySet<string> {
  const set = new Set<string>();
  for (const ind of INDICATORS) {
    if (turn >= (ind.revealedFromTurn ?? 0)) {
      set.add(ind.id);
    }
  }
  for (const [id, threshold] of Object.entries(REVEAL_BY_DECISION_AT_TURN)) {
    if (turn >= threshold) set.add(id);
  }
  return set;
}

export function ProblemZone({ state }: Props) {
  const decision = useGame((s) => s.currentDecision());
  const pickOption = useGame((s) => s.pickOption);

  // ScrollTo (2026-07-15) : quand une NOUVELLE question arrive (on vient de
  // répondre), recadrer la vue juste AU-DESSUS du bandeau déroulant (télex)
  // — la grille place le ticker immédiatement avant la question, on voit
  // donc le bandeau puis la question dessous. Pas de scroll au premier
  // rendu (chargement).
  const prevDecisionId = useRef<string | null>(null);
  useEffect(() => {
    const id = decision?.id ?? null;
    if (id && prevDecisionId.current && id !== prevDecisionId.current) {
      document
        .querySelector(".press-ticker")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    if (id) prevDecisionId.current = id;
  }, [decision?.id]);

  if (decision) {
    return (
      <DecisionCard decision={decision} turn={state.turn} onPick={pickOption} />
    );
  }

  if (state.turn === 0) {
    return (
      <section className="problem" data-kind="intro">
        <div className="problem-kicker">PRISE DE POUVOIR · TOUR 0</div>
        <h3 className="problem-title">
          Vous entrez en fonction. Le pays vous est confié.
        </h3>
        <p className="problem-hint">
          Votre cockpit à droite montre trois indicateurs visibles — d'autres
          émergeront. Cliquez sur <em>Tour suivant</em> pour commencer.
        </p>
      </section>
    );
  }

  return (
    <section className="problem" data-kind="idle">
      <div className="problem-kicker">TOUR {state.turn} · PAUSE</div>
      <h3 className="problem-title">Aucune question pressante ce mois-ci.</h3>
      <p className="problem-hint">
        Le sélecteur attend que la situation mûrisse — cliquez sur{" "}
        <em>Tour suivant</em>
        pour laisser le temps passer. Une nouvelle décision émergera lorsque les
        conditions l'exigeront.
      </p>
    </section>
  );
}

function DecisionCard({
  decision,
  turn,
  onPick,
}: {
  decision: Decision;
  turn: number;
  onPick: (optionId: string) => void;
}) {
  const revealed = useMemo(() => revealedAt(turn), [turn]);

  // Leçons de mécanismes apprises — rappel « Vous savez » sous les
  // options que le joueur a déjà comprises (vérité assistée, moteur).
  const learnedLessons = useGame((st) => st.worldState?.polity.learned_lessons);
  const lessonsByOption = useMemo(() => {
    const map = new Map<string, NonNullable<typeof learnedLessons>[number]>();
    for (const l of learnedLessons ?? []) map.set(l.option_id, l);
    return map;
  }, [learnedLessons]);
  // État local : option en cours de prévisualisation.
  // Reset à chaque nouvelle Decision (key sur l'id côté parent suffit en
  // général, mais on capture l'id en dépendance pour être explicite).
  const [previewedId, setPreviewedId] = useState<string | null>(null);
  useEffect(() => {
    setPreviewedId(null);
  }, [decision.id]);

  // Charge les tropes une fois (cache module-level).
  const [library, setLibrary] = useState<TropesLibrary | null>(null);
  useEffect(() => {
    let mounted = true;
    loadTropes()
      .then((lib) => {
        if (mounted) setLibrary(lib);
      })
      .catch(() => {
        /* déjà loggé dans loadTropes */
      });
    return () => {
      mounted = false;
    };
  }, []);

  const previewedOption = useMemo<DecisionOption | null>(() => {
    if (!previewedId) return null;
    return decision.options.find((o) => o.id === previewedId) ?? null;
  }, [decision, previewedId]);

  // R4 (2026-07-16, étude restitution) — le COÛT BUDGÉTAIRE de l'option
  // est visible AVANT le choix. C'était le trou le plus flagrant : une
  // option à 45 Md€ s'affichait sans un mot sur l'argent.
  const fiscalChip = useMemo<string | null>(() => {
    const fi = previewedOption?.fiscal_impact as
      | { delta_revenue_billions?: number; delta_expense_billions?: number }
      | undefined;
    if (!fi) return null;
    const rev = fi.delta_revenue_billions ?? 0;
    const exp = fi.delta_expense_billions ?? 0;
    const net = rev - exp;
    if (Math.abs(net) < 0.05 && Math.abs(rev) < 0.05 && Math.abs(exp) < 0.05)
      return null;
    const fmt = (n: number) =>
      Math.abs(n) >= 10
        ? Math.round(Math.abs(n)).toString()
        : Math.abs(n).toFixed(1);
    const parts: string[] = [];
    if (exp > 0.05) parts.push(`+${fmt(exp)} Md€ de dépenses`);
    if (exp < -0.05) parts.push(`−${fmt(exp)} Md€ de dépenses`);
    if (rev > 0.05) parts.push(`+${fmt(rev)} Md€ de recettes`);
    if (rev < -0.05) parts.push(`−${fmt(rev)} Md€ de recettes`);
    const sign = net > 0 ? "améliore" : "creuse";
    return `Budget : ${parts.join(", ")} (${sign} le solde de ${fmt(net)} Md€/an)`;
  }, [previewedOption]);

  const impactPhrase = useMemo<string | null>(() => {
    if (!previewedOption || !library) return null;
    return summarizeOption(previewedOption, library, revealed, decision.domain);
  }, [previewedOption, library, revealed]);

  const togglePreview = (optionId: string) => {
    setPreviewedId((prev) => (prev === optionId ? null : optionId));
  };

  const validate = () => {
    if (previewedId) {
      onPick(previewedId);
    }
  };

  // « Agir » — si cette décision est un redressement déclenché depuis un
  // indicateur, on l'encadre d'une bannière d'intervention (avec sortie).
  const activeRemediation = useGame((s) => s.activeRemediation);
  const cancelRemediationFlow = useGame((s) => s.cancelRemediationFlow);
  const remediatedLabel = activeRemediation
    ? (INDICATORS.find((i) => i.id === activeRemediation.indicatorId)?.label ??
      activeRemediation.indicatorId)
    : null;

  // La Bascule — une opportunité saisie devient cette décision. On
  // l'encadre comme l'intervention « Agir » : bannière + sortie (renoncer
  // restaure la décision normale ; l'occasion, elle, est consommée).
  const activeBascule = useGame((s) => s.activeBascule);
  const cancelBascule = useGame((s) => s.cancelBascule);
  const isBascule = activeBascule === decision.id;

  return (
    <section
      className="problem"
      data-kind={decision.kind}
      data-remediation={activeRemediation ? "true" : undefined}
      data-bascule={isBascule ? "true" : undefined}
    >
      {activeRemediation && (
        <div className="remediation-banner">
          <span className="remediation-banner-label">
            ⚡ Intervention d'urgence — {remediatedLabel}
          </span>
          <button
            type="button"
            className="remediation-banner-cancel"
            onClick={cancelRemediationFlow}
          >
            ← Renoncer
          </button>
        </div>
      )}
      {isBascule && (
        <div className="bascule-banner">
          <span className="bascule-banner-label">
            ✦ Occasion exceptionnelle — un pari à double tranchant
          </span>
          <button
            type="button"
            className="bascule-banner-cancel"
            onClick={cancelBascule}
          >
            ← Renoncer
          </button>
        </div>
      )}
      <div className="problem-kicker">{decision.kicker}</div>
      <h3 className="problem-title">{decision.prompt}</h3>

      {decision.tags && decision.tags.length > 0 && (
        <div className="problem-tags" aria-label="Thématiques">
          {decision.tags.slice(0, 5).map((tag) => (
            <span key={tag} className="problem-tag">
              #{tag}
            </span>
          ))}
        </div>
      )}

      {decision.context && (
        <p className="problem-context">{decision.context}</p>
      )}

      {decision.glossary && decision.glossary.length > 0 && (
        <dl className="problem-glossary" aria-label="Glossaire">
          {decision.glossary.map((g) => (
            <div key={g.term} className="gloss-entry">
              <dt className="gloss-term">{g.term}</dt>
              <dd className="gloss-def">{g.def}</dd>
            </div>
          ))}
        </dl>
      )}

      <div className="problem-options-head" aria-hidden="true">
        <span className="poh-rule" />
        <span className="poh-text">
          ✦&nbsp; Les décisions proposées au Président &nbsp;✦
        </span>
        <span className="poh-rule" />
      </div>

      <div className="problem-options">
        {decision.options.map((opt, i) => {
          const selected = opt.id === previewedId;
          const lesson = lessonsByOption.get(opt.id);
          const cost = optionCost(opt);
          return (
            <button
              key={opt.id}
              type="button"
              className={`frame opt ${opt.position === "primary" ? "opt-primary" : ""} ${selected ? "opt-selected" : ""}`}
              data-frame={selected ? "panel" : "paper"}
              data-corners="diagonal"
              aria-pressed={selected}
              onClick={() => togglePreview(opt.id)}
            >
              <span className="opt-head">
                <span className="opt-rank">
                  {selected ? "✦" : "✧"} Décision {ROMAN[i] ?? i + 1}
                </span>
                <span className="opt-kind">{optionKind(opt)}</span>
              </span>
              <span className="opt-title">{opt.label}</span>
              {opt.impact_summary && (
                <span className="opt-body">{opt.impact_summary}</span>
              )}
              {cost && (
                <span className="opt-cost" data-tone={cost.tone}>
                  {cost.text}
                </span>
              )}
              {lesson && (
                <span className="opt-lesson" title={lesson.narrative}>
                  📒 Vous savez : {lesson.effects.join(" · ")}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div className="problem-impact" data-empty={!impactPhrase}>
        {impactPhrase ? (
          <>
            <span className="impact-prefix">Si vous tranchez ainsi :</span>
            <span className="impact-phrase">{impactPhrase}</span>
            {fiscalChip && <span className="impact-fiscal">{fiscalChip}</span>}
          </>
        ) : (
          <span className="impact-placeholder">
            Cliquez sur une option pour en lire les conséquences.
          </span>
        )}
      </div>

      <button
        type="button"
        className="problem-validate"
        onClick={validate}
        disabled={!previewedId}
      >
        ━━ VALIDER LA DÉCISION ━━
      </button>
    </section>
  );
}
